import { useEffect, useState } from 'react';
import { ChiPhiMuaHang, ChiPhiMuaHangInput, ChiPhiMuaHangResponse } from '@/types/schemas/chi-phi-mua-hang.type';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

interface UseChiPhiMuaHangReturn {
  chiPhiMuaHangs: ChiPhiMuaHang[];
  isLoading: boolean;
  addChiPhiMuaHang: (newChiPhiMuaHang: ChiPhiMuaHangInput) => Promise<void>;
  updateChiPhiMuaHang: (uuid: string, updatedChiPhiMuaHang: ChiPhiMuaHangInput) => Promise<void>;
  deleteChiPhiMuaHang: (uuid: string) => Promise<void>;
  refreshChiPhiMuaHangs: () => Promise<void>;
}

export const useChiPhiMuaHang = (initialChiPhiMuaHangs: ChiPhiMuaHang[] = []): UseChiPhiMuaHangReturn => {
  const [chiPhiMuaHangs, setChiPhiMuaHangs] = useState<ChiPhiMuaHang[]>(initialChiPhiMuaHangs);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { entity } = useAuth();

  const fetchChiPhiMuaHangs = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get<ChiPhiMuaHangResponse>(`/entities/${entity.slug}/erp/expenses/`);
      setChiPhiMuaHangs(response.data.results);
    } catch (error) {
      console.error('Error fetching chi phi mua hangs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addChiPhiMuaHang = async (newChiPhiMuaHang: ChiPhiMuaHangInput) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.post(`/entities/${entity.slug}/erp/expenses/`, {
        ma_cp: newChiPhiMuaHang.ma_cp,
        ten_cp: newChiPhiMuaHang.ten_cp,
        ten_cp2: newChiPhiMuaHang.ten_cp2 || null,
        loai_cp: newChiPhiMuaHang.loai_cp,
        loai_pb: newChiPhiMuaHang.loai_pb,
        ma_ct: newChiPhiMuaHang.ma_ct,
        status: newChiPhiMuaHang.status
      });

      const addedChiPhiMuaHang: ChiPhiMuaHang = response.data;
      setChiPhiMuaHangs(prev => [...prev, addedChiPhiMuaHang]);
    } catch (error) {
      console.error('Error adding chi phi mua hang:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateChiPhiMuaHang = async (uuid: string, updatedChiPhiMuaHang: ChiPhiMuaHangInput) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.put(`/entities/${entity.slug}/erp/expenses/${uuid}/`, {
        ma_cp: updatedChiPhiMuaHang.ma_cp,
        ten_cp: updatedChiPhiMuaHang.ten_cp,
        ten_cp2: updatedChiPhiMuaHang.ten_cp2 || null,
        loai_cp: updatedChiPhiMuaHang.loai_cp,
        loai_pb: updatedChiPhiMuaHang.loai_pb,
        ma_ct: updatedChiPhiMuaHang.ma_ct,
        status: updatedChiPhiMuaHang.status
      });

      // Update the chi phi mua hang in the list
      const updatedChiPhiMuaHangData: ChiPhiMuaHang = response.data;

      setChiPhiMuaHangs(prev =>
        prev.map(item => (item.uuid === updatedChiPhiMuaHangData.uuid ? updatedChiPhiMuaHangData : item))
      );
    } catch (error) {
      console.error('Error updating chi phi mua hang:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteChiPhiMuaHang = async (uuid: string) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/expenses/${uuid}/`);

      // Remove the chi phi mua hang from the list
      setChiPhiMuaHangs(prev => prev.filter(item => item.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting chi phi mua hang:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchChiPhiMuaHangs();
  }, [entity?.slug]);

  return {
    chiPhiMuaHangs,
    isLoading,
    addChiPhiMuaHang,
    updateChiPhiMuaHang,
    deleteChiPhiMuaHang,
    refreshChiPhiMuaHangs: fetchChiPhiMuaHangs
  };
};
